import * as React from 'react'
import styles from './index.module.scss'
import clsx from 'clsx'
import { capitalize } from '../../utils/capitalize'
import { TypographySxProps, SxProps, sx as sxFunc } from '../sx'

const componentMapping = {
  displayXLarge: 'h1',
  displayLarge: 'h2',
  displayMedium: 'h3',
  displaySmall: 'h4',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  p: 'p',
  bodyLarge: 'p',
  bodyMedium: 'p',
  bodySmall: 'p',
  span: 'span',
  label: 'label',
} as const

const variantMapping = {
  displayXLarge: 'display-xlarge',
  displayLarge: 'display-large',
  displayMedium: 'display-medium',
  displaySmall: 'display-small',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  p: 'body-small',
  bodyLarge: 'body-large',
  bodyMedium: 'body-medium',
  bodySmall: 'body-small',
  span: 'inherit',
  label: 'label',
} as const

type ComponentKeys = keyof typeof componentMapping
type ComponentValues = (typeof componentMapping)[ComponentKeys]

type VariantKeys = keyof typeof variantMapping

export interface TypographyClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the root element if `align="left"`. */
  alignLeft: string
  /** Styles applied to the root element if `align="center"`. */
  alignCenter: string
  /** Styles applied to the root element if `align="right"`. */
  alignRight: string
  /** Styles applied to the root element if `align="justify"`. */
  alignJustify: string
  /** Styles applied to the root element if `align="inherit"`. */
  alignInherit: string
  /** Styles applied to the root element if `nowrap={true}`. */
  noWrap: string
  /** Styles applied to the root element if `gutterBottom={true}`. */
  gutterBottom: string
}

export interface TypographyProps
  extends Partial<React.HTMLAttributes<HTMLSpanElement>> {
  /**
   * Set the text-align on the component. we can set responsive align options as like we have in sx-typography
   * @default 'inherit'
   */
  align?: TypographySxProps['textAlign']
  /**
   * Set the text-weight on the component.
   */
  weight?: TypographySxProps['textWeight']
  /**
   * lineHeight - property specifies the height of a line.
   */
  lineHeight?: TypographySxProps['lineHeight']
  /**
   * The content of the component.
   */
  children: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<TypographyClasses>
  /**
   * If `true`, the text will have a bottom margin.
   * @default true
   */
  gutterBottom?: boolean
  /**
   * If `true`, the text will not wrap, but instead will truncate with a text overflow ellipsis.
   *
   * Note that text overflow can only happen with block or inline-block level elements
   * (the element needs to have a width in order to overflow).
   * @default false
   */
  noWrap?: boolean
  /**
   * Applies the theme typography styles.
   * @default 'span'
   */
  variant?: VariantKeys
  /**
   * Component to render
   * @default 'span'
   */
  component?: ComponentValues | React.ElementType
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
}
export const Typography = React.forwardRef<HTMLDivElement, TypographyProps>(
  (props, ref) => {
    const {
      align,
      weight,
      lineHeight,
      className,
      component,
      gutterBottom = true,
      noWrap = false,
      variant = 'span',
      children,
      classes,
      sx,
      ...other
    } = props
    // If the Component exists in either component or componentMapping, we use it. Otherwise, use span tag instead.
    const Component =
      component || (componentMapping[variant] as ComponentValues) || 'span'
    return (
      <Component
        ref={ref}
        className={clsx(
          align && [getTypographyClasses('align', align, classes)],
          weight && getTypographyClasses('weight', weight),
          lineHeight && getTypographyClasses('lineHeight', lineHeight),
          styles[variantMapping[variant]],
          gutterBottom && classes?.gutterBottom,
          !gutterBottom && styles.noGutterBottom,
          noWrap && [styles['noWrap'], classes?.noWrap],
          className && className,
          sxFunc(sx)
        )}
        {...other}
      >
        {children}
      </Component>
    )
  }
)
Typography.displayName = 'Typography'

const getTypographyClasses = (
  classPrefix: string,
  classModifier?:
    | TypographySxProps['textAlign']
    | TypographySxProps['textWeight']
    | TypographySxProps['lineHeight'],
  classes?: Partial<TypographyClasses>
) => {
  const viewPortModifiers = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl'] as const
  if (classPrefix && typeof classModifier === 'object') {
    return viewPortModifiers
      .map(viewPortModifier => {
        if (viewPortModifier === 'xs' && classModifier[viewPortModifier]) {
          return clsx(
            styles[`${classPrefix}-${classModifier[viewPortModifier]}`],
            // @ts-ignore
            classes?.[
              // @ts-ignore
              `${classPrefix}${capitalize(classModifier[viewPortModifier])}`
            ]
          )
        } else if (classModifier[viewPortModifier]) {
          return clsx(
            styles[
              `${classPrefix}-${viewPortModifier}-${classModifier[viewPortModifier]}`
            ],
            // @ts-ignore
            classes?.[
              // @ts-ignore
              `${classPrefix}${capitalize(classModifier[viewPortModifier])}`
            ]
          )
        }
      })
      .filter(Boolean)
      .join(' ')
  } else if (classPrefix && classModifier) {
    return clsx(
      styles[`${classPrefix}-${classModifier}`],
      // @ts-ignore
      classes?.[`${classPrefix}${capitalize(classModifier)}`]
    )
  }
}
